import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, LockKey } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Profile() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update");
    }
    setBusy(false);
  };

  return (
    <ClientShell>
      <Link to="/more" className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6" data-testid="profile-back">
        <ArrowLeft size={14} /> Back to More
      </Link>

      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Account</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">Profile</h1>
      </div>

      <div className="grid gap-6 max-w-2xl">
        <div className="bg-white border border-zinc-200 p-6 md:p-8">
          <div className="mt-tiny text-zinc-500 mb-4">Account details</div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" value={user?.name} />
            <Field label="Email" value={user?.email} mono />
            <Field label="Company" value={user?.company || "—"} />
            <Field label="Role" value={(user?.role || "").toUpperCase()} />
          </dl>
        </div>

        <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 md:p-8" data-testid="change-password-form">
          <div className="flex items-center gap-2 mb-4">
            <LockKey size={18} className="text-[#0047AB]" />
            <div className="font-display font-bold text-zinc-900">Change password</div>
          </div>
          <div className="grid gap-4">
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Current password</div>
              <Input type="password" data-testid="current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} className="h-11 rounded-none border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">New password</div>
              <Input type="password" data-testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} className="h-11 rounded-none border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Confirm new password</div>
              <Input type="password" data-testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 rounded-none border-zinc-200" />
            </div>
            <div>
              <button type="submit" disabled={busy} data-testid="change-password-submit" className="bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60">
                {busy ? "Updating..." : "Update password"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </ClientShell>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <dt className="mt-tiny text-zinc-400">{label}</dt>
      <dd className={`mt-1 text-zinc-900 ${mono ? "font-mono-mt text-sm" : "font-medium"}`}>{value}</dd>
    </div>
  );
}
